import {
  EmptyAPIData,
  LoadingAPIData,
  APIData,
  emptyResults,
  loadingResults,
  APIActionGeneratorOptions,
  APIAction,
  APIRequest,
  BaseAPIActionGenerator,
} from '../apidata';
import { APIResult } from '../api';
import { ForkEffect } from '@redux-saga/core/effects';
import { MapIndirectOptions, MapIndirectPaginationFunction } from './map';

/** type representing a single page of results */
export type PaginatedAPIResult<P> = {
  nextPage?: string;
  previousPage?: string;
  results: P[];
};

/** type representing empty paginated data */
export type EmptyPaginatedAPIData = EmptyAPIData;

/** type represented paginated data that's currently loading results */
export type LoadingPaginatedAPIData = LoadingAPIData;

/** base type representing paginated data that has some results to show */
export type FilledPaginatedAPIDataBase<P, E = unknown> = {
  isLoading: false;
  isFilled: true;
  isEmpty: false;

  /** in-order list of pages that have been queried */
  pageQueries: string[];

  /** mapping of pageQuery strings above to the page data itself */
  pageResults: Record<string, APIData<PaginatedAPIResult<P>, E>>;

  /** total number of results currently queried */
  resultsCount: number;

  /** previous page to query */
  previousPage?: string;

  /** next page to query */
  nextPage?: string;
};

/** type representing successfully queried paginated data */
export type SuccessfulPaginatedAPIData<
  P,
  E = unknown
> = FilledPaginatedAPIDataBase<P, E> & {
  success: true;
};

/** type representing paginated data for which the last query had an error */
export type ErroredPaginatedAPIData<
  P,
  E = unknown
> = FilledPaginatedAPIDataBase<P, E> & {
  success: false;
  errorData: E;
};

/** type representing paginated data that has results that can be used */
export type FilledPaginatedAPIData<P, E = unknown> =
  | SuccessfulPaginatedAPIData<P, E>
  | ErroredPaginatedAPIData<P, E>;

/** type representing all states of paginated data */
export type PaginatedAPIData<P, E = unknown> =
  | EmptyPaginatedAPIData
  | LoadingPaginatedAPIData
  | SuccessfulPaginatedAPIData<P, E>
  | ErroredPaginatedAPIData<P, E>;

/** type representing paginated data organized by unique strings */
export type IndirectPaginatedAPIData<P, E = unknown> = Record<
  string,
  PaginatedAPIData<P, E>
>;

/**
 * Constant containing empty paginated data so a new object doesn't need to be
 * created when it's needed
 */
export const emptyPaginatedAPIData: EmptyPaginatedAPIData = emptyResults;

/**
 * Constant containing loading paginated data indicating tht the first page is
 * currently being loaded.
 */
export const loadingPaginatedAPIData: LoadingPaginatedAPIData = loadingResults;

/**
 * Constant containing empty indirect paginated data so a new object doesn't
 * need to be created when it's needed
 */
export const emptyIndirectPaginatedAPIData = {};

/** Options to use when creating a pagination generator */
export type PaginationOptions<
  R extends PaginatedAPIRequest<T, E>,
  T,
  P,
  E = unknown
> = APIActionGeneratorOptions & {
  /**
   * Translate a returned page to a format that can be interpreted by this
   * pagination layer.
   */
  translatePage: (page: T) => PaginatedAPIResult<P>;

  /**
   * (optional) Generate the page ID from the given request. This will be used
   * to identify a specific page.
   */
  getPageID?: (request: R) => string;
};

/** Options to use when creating an indirect pagination generator */
export type IndirectPaginationOptions<
  R extends PaginatedAPIRequest<T, E>,
  T,
  P,
  E = unknown
> = PaginationOptions<R, T, P, E> & {
  /**
   * Hook for determining the indirect "key" from the request so that the
   * paginated data can be organized properly.
   */
  getKey: (request: R) => string;
};

export type PaginationSagaOptions<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  E = unknown
> = {
  generatePage: PageGenerator<R>;
  onSuccess?: (
    request: R,
    response: APIResult<T, E>
  ) => Generator<any, any, any> | void;
};

/** Request to fetch a paginated API */
export type PaginatedAPIRequest<T = unknown, E = unknown> = APIRequest<T, E> & {
  /** page to explicitly fetch */
  page?: string;
  /** whether or not to fetch all pages at once */
  fetchAll?: boolean;
  /** reset "next" state and fetch the first page again */
  reset?: boolean;
};

/** Store function for manipulating PaginatedAPIData state */
export type PageStoreFunction<
  R = unknown,
  T = unknown,
  P = unknown,
  E = unknown
> = (
  state: PaginatedAPIData<P, E>,
  action: APIAction<R, T, E>
) => PaginatedAPIData<P, E>;

/** Store function for manipulating IndirectPaginatedAPIData state */
export type IndirectPageStoreFunction<
  R = unknown,
  T = unknown,
  P = unknown,
  E = unknown
> = (
  state: IndirectPaginatedAPIData<P, E>,
  action: APIAction<R, T, E>
) => IndirectPaginatedAPIData<P, E>;

/** Selector for returning PaginatedAPIData state */
export type PaginationSelector<R = unknown, P = unknown, E = unknown> = (
  state: any,
  request: R
) => PaginatedAPIData<P, E>;

export type BasePaginationAPIActionGenerator<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  E = unknown
> = BaseAPIActionGenerator<R, T, E>;

/** type representing a generator for paginated results */
export type PaginationAPIActionGenerator<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  P = unknown,
  E = unknown
> = BasePaginationAPIActionGenerator<R, T, E> & {
  /** Create a store that handles actions for the given generator */
  store: () => PageStoreFunction<R, T, P, E>;

  /**
   * Function to generate a takeEvery that handles the "request" of the given
   * APITypes with customizeable options.
   *
   * @param sagaOpts - options to customize the saga operation
   */
  customSaga: (sagaOpts: PaginationSagaOptions<R, T, E>) => ForkEffect;

  /**
   * Function to generate a takeEvery that handles the "request" of the given
   * APITypes.
   *
   * @param getInitialURL - function for querying the URL for the first page
   * @param selector - selector for querying the page state
   */
  saga: (
    getInitialURL: (request: R) => string,
    selector: PaginationSelector<R, P, E>
  ) => ForkEffect;

  mapIndirect: (
    mapOpts: MapIndirectOptions<P>
  ) => MapIndirectPaginationFunction<T, P>;
};

/** type representing a generator for indirect paginated results */
export type IndirectPaginationAPIActionGenerator<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  P = unknown,
  E = unknown
> = Omit<PaginationAPIActionGenerator<R, T, P, E>, 'store'> & {
  /** Create a store that handles actions for the given generator */
  store: () => IndirectPageStoreFunction<R, T, P, E>;
};

export type PageGenerator<R = unknown> = (
  request: R
) => (string | undefined) | Generator<any, string | undefined, any>;

/** Query and return the number of page entries for the given state data */
export function getResultsCount<P = unknown, E = unknown>(
  state: PaginatedAPIData<P, E>
): number {
  if (!state.isFilled) {
    return 0;
  }
  return state.resultsCount;
}
